import * as Tooltip from "@radix-ui/react-tooltip";
import React, { useId } from "react";
import { cn } from "../../lib/utils";
import { LucideIcon } from "lucide-react";

interface Props {
  label: string;
  icon: LucideIcon;
  isActive: boolean;
  onClick: () => void;
  isCollapsed: boolean;
  disabled?: boolean;
}

const NavButton: React.FC<Props> = ({
  label,
  icon: Icon,
  isActive,
  onClick,
  isCollapsed,
  disabled=false,
}) => {

  const gradientId = useId();

  const triggerButton = (
    <button
      type="button"
      className={cn(
        "flex items-center whitespace-nowrap text-xs font-normal",
        "w-full h-8 p-2 rounded-[8px]",
        "transition-colors duration-100",
        isCollapsed ? "justify-center" : "justify-start gap-[6px]",
        isActive ? "bg-[#F5F5F5]" : "bg-transparent hover:bg-[#F5F5F5]",
        disabled ? "opacity-90 cursor-not-allowed" : ""
      )}
      style={{ maxWidth: isCollapsed ? undefined : "240px" }}
      disabled={disabled}
      onClick={onClick}
    >
      <span className="h-[14px] w-[14px] flex-shrink-0 flex items-center justify-center">
        <Icon
          size={14}
          style={{
            stroke: isActive ? `url(#${gradientId})` : "#6B7280",
          }}
          strokeWidth={1.8}
        />
      </span>

      {!isCollapsed && (
        <span
          className={cn(
            "truncate leading-none",
            "text-[13px] font-medium",
            isActive ? "text-[#0A0A0A]" : "text-[#6B7280]"
          )}
        >
          {label}
        </span>
      )}
    </button>
  );

  const defs = (
    <svg width="0" height="0" style={{ position: "absolute" }}>
      <defs>
        <linearGradient
          id={gradientId}
          x1="0"
          y1="0"
          x2="0"
          y2="14"
          gradientUnits="userSpaceOnUse"
        >
          <stop offset="0%" stopColor="#788AF7" />
          <stop offset="100%" stopColor="#3551F3" />
        </linearGradient>
      </defs>
    </svg>
  );

  if (!isCollapsed) {
    return (
      <>
        {defs}
        {triggerButton}
      </>
    );
  }

  return (
    <>
      {defs}
      <Tooltip.Provider delayDuration={10} skipDelayDuration={200}>
        <Tooltip.Root>
          <Tooltip.Trigger asChild>
            {disabled ? (
              <span className="inline-flex w-full max-w-full cursor-not-allowed">
                {triggerButton}
              </span>
            ) : (
              triggerButton
            )}
          </Tooltip.Trigger>
          <Tooltip.Portal>
            <Tooltip.Content
              side="right"
              align="center"
              sideOffset={10}
              className="z-[9999] max-w-[280px] px-3 py-2 rounded-md bg-[#374151] text-[#FAFAFA] text-xs font-normal leading-snug shadow-md select-none"
            >
              {label}
              <Tooltip.Arrow className="fill-[#374151]" />
            </Tooltip.Content>
          </Tooltip.Portal>
        </Tooltip.Root>
      </Tooltip.Provider>
    </>
  );
};

export default NavButton;